import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ForeignNationalDocumentsAPI } from "../../../api/foreignNationalDocumentsApi";
import type {
  ForeignNationalDocument,
  ForeignNationalDocumentFilingStatus
} from "../types";

type UseForeignNationalDocumentsResult = {
  documents: ForeignNationalDocument[];
  isLoading: boolean;
  errorMessage: string | null;
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
  upload: (
    file: File,
    opts?: {
      signal?: AbortSignal;
      onProgress?: (args: {
        percent: number;
        loaded: number;
        total: number;
      }) => void;
    }
  ) => Promise<ForeignNationalDocument>;
  remove: (foreignNationalDocumentId: string) => Promise<void>;
  updateFilingStatus: (
    foreignNationalDocumentId: string,
    filingStatus: ForeignNationalDocumentFilingStatus
  ) => Promise<void>;
  setShared: (
    foreignNationalDocumentId: string,
    shared: boolean
  ) => Promise<void>;
};

export function useForeignNationalDocuments(
  foreignNationalId: string | null
): UseForeignNationalDocumentsResult {
  const [documents, setDocuments] = useState<ForeignNationalDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const refreshSeqRef = useRef(0);
  const mutationSeqRef = useRef(0);

  const refresh = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!foreignNationalId) return;
      const silent = Boolean(opts?.silent);
      refreshSeqRef.current += 1;
      const refreshSeq = refreshSeqRef.current;
      const startedMutationSeq = mutationSeqRef.current;
      if (!silent) setIsLoading(true);
      if (!silent) setErrorMessage(null);
      try {
        const data = await ForeignNationalDocumentsAPI.list(foreignNationalId);
        if (refreshSeqRef.current !== refreshSeq) return;
        setDocuments((prev) => {
          if (mutationSeqRef.current !== startedMutationSeq) {
            // Merge without introducing row flicker:
            // - preserve `prev` ordering
            // - overwrite values with server versions when present
            // - append any new server docs that weren't in `prev`
            const merged = new Map<string, ForeignNationalDocument>();
            for (const d of prev) {
              merged.set(d.foreign_national_document_id, d);
            }
            for (const d of data) {
              merged.set(d.foreign_national_document_id, d);
            }
            return Array.from(merged.values());
          }
          return data;
        });
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to load documents";
        setErrorMessage(msg);
        throw new Error(msg);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [foreignNationalId]
  );

  useEffect(() => {
    refresh().catch(() => undefined);
  }, [refresh]);

  const upload = useCallback(
    async (
      file: File,
      opts?: {
        signal?: AbortSignal;
        onProgress?: (args: {
          percent: number;
          loaded: number;
          total: number;
        }) => void;
      }
    ) => {
      if (!foreignNationalId) throw new Error("Missing foreignNationalId");
      setErrorMessage(null);
      try {
        const created = await ForeignNationalDocumentsAPI.upload(
          foreignNationalId,
          file,
          opts
        );
        mutationSeqRef.current += 1;
        setDocuments((prev) => {
          const withoutDuplicate = prev.filter(
            (d) =>
              d.foreign_national_document_id !==
              created.foreign_national_document_id
          );
          return [created, ...withoutDuplicate];
        });
        // Avoid immediate refresh here: the server list can lag briefly, which causes
        // the newly uploaded row to flicker (appear/disappear). The upload response
        // is the final record; nothing is processed afterwards.
        return created;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        setErrorMessage(msg);
        throw new Error(msg);
      }
    },
    [foreignNationalId]
  );

  const remove = useCallback(
    async (foreignNationalDocumentId: string) => {
      if (!foreignNationalId) throw new Error("Missing foreignNationalId");
      setErrorMessage(null);
      try {
        await ForeignNationalDocumentsAPI.remove(
          foreignNationalId,
          foreignNationalDocumentId
        );
        mutationSeqRef.current += 1;
        setDocuments((prev) =>
          prev.filter(
            (d) => d.foreign_national_document_id !== foreignNationalDocumentId
          )
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Delete failed";
        setErrorMessage(msg);
        throw new Error(msg);
      }
    },
    [foreignNationalId]
  );

  const updateFilingStatus = useCallback(
    async (
      foreignNationalDocumentId: string,
      filingStatus: ForeignNationalDocumentFilingStatus
    ) => {
      if (!foreignNationalId) throw new Error("Missing foreignNationalId");
      setErrorMessage(null);
      try {
        const updated = await ForeignNationalDocumentsAPI.updateFilingStatus(
          foreignNationalId,
          foreignNationalDocumentId,
          filingStatus
        );
        mutationSeqRef.current += 1;
        setDocuments((prev) =>
          prev.map((d) =>
            d.foreign_national_document_id === foreignNationalDocumentId
              ? updated
              : d
          )
        );
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to update status";
        setErrorMessage(msg);
        throw new Error(msg);
      }
    },
    [foreignNationalId]
  );

  const setShared = useCallback(
    async (foreignNationalDocumentId: string, shared: boolean) => {
      if (!foreignNationalId) return;
      const id = foreignNationalDocumentId;
      setDocuments((prev) =>
        prev.map((d) =>
          d.foreign_national_document_id === id
            ? { ...d, shared_with_client: shared }
            : d
        )
      );
      try {
        const updated = await ForeignNationalDocumentsAPI.setShared(
          foreignNationalId,
          id,
          shared
        );
        setDocuments((prev) =>
          prev.map((d) => (d.foreign_national_document_id === id ? updated : d))
        );
      } catch (err) {
        setDocuments((prev) =>
          prev.map((d) =>
            d.foreign_national_document_id === id
              ? { ...d, shared_with_client: !shared }
              : d
          )
        );
        throw err instanceof Error ? err : new Error("Couldn't update sharing");
      }
    },
    [foreignNationalId]
  );

  return useMemo(
    () => ({
      documents,
      isLoading,
      errorMessage,
      refresh,
      upload,
      remove,
      updateFilingStatus,
      setShared
    }),
    [
      documents,
      errorMessage,
      isLoading,
      refresh,
      remove,
      setShared,
      updateFilingStatus,
      upload
    ]
  );
}
